import React, { useId } from "react";
import { cn } from "@/lib/utils";

interface CirclePartialFillIconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
  color?: string;
  startAngle?: number; // degrees, 0 = right, 90 = top
  endAngle?: number; // degrees
}

export const CirclePartialFillIcon: React.FC<CirclePartialFillIconProps> = ({
  className,
  color = "#CA8A04",
  startAngle = 0,
  endAngle = 90,
  ...props
}) => {
  const radius = 4.0833;
  const centerX = 10;
  const centerY = 10;

  // Normalize angles to be between 0 and 360
  const normalizedStart = ((startAngle % 360) + 360) % 360;
  let normalizedEnd = ((endAngle % 360) + 360) % 360;
  if (normalizedEnd <= normalizedStart) normalizedEnd += 360;

  // Generate points along the arc
  const numPoints = 16; // More points = smoother arc
  const points: [number, number][] = [];

  for (let i = 0; i <= numPoints; i++) {
    const angle =
      normalizedStart + (i / numPoints) * (normalizedEnd - normalizedStart);
    const rad = (angle * Math.PI) / 180;
    const x = centerX + radius * Math.cos(rad);
    const y = centerY - radius * Math.sin(rad);
    points.push([x, y]);
  }

  // Build the path
  const pathCommands = [
    `M ${points[0][0]} ${points[0][1]}`, // Move to start
    ...points.slice(1).map(([x, y]) => `L ${x} ${y}`), // Draw lines between points
    `L ${centerX} ${centerY}`, // Line to center
    "Z", // Close path
  ];

  const partialFillPath = pathCommands.join(" ");

  const clipId = useId();

  return (
    <svg
      width="20"
      height="20"
      className={className}
      viewBox="0 0 20 20"
      fill="none"
    >
      <g clipPath={`url(#${clipId})`}>
        <path
          d="M9.99996 15.8334C13.2216 15.8334 15.8333 13.2217 15.8333 10.0001C15.8333 6.77842 13.2216 4.16675 9.99996 4.16675C6.7783 4.16675 4.16663 6.77842 4.16663 10.0001C4.16663 13.2217 6.7783 15.8334 9.99996 15.8334Z"
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d={partialFillPath} fill={color} />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect
            width="14"
            height="14"
            fill="white"
            transform="translate(3 3)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};
